import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getCrudStatusLabel, localStorageKeys, permissionLevelEnum } from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { getSiteTypeLabel } from '../../../../constants/labels/site';
import { useUpdateSiteMutation } from '../../../../hooks/mutations/useSiteMutation';
import { useEntitiesQuery } from '../../../../hooks/queries/useEntitiesQuery';
import { useSitesQuery } from '../../../../hooks/queries/useSitesQuery';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { Site } from '../../../../services/apiSLTP/sites/sites';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { formatNumber } from '../../../../utils/numbers';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateSiteModal from '../../../modals/admin/internal/UpdateSiteModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface SitesSettingsRowProps {
    withUsageCount: boolean;
}

const SitesSettingsRow: React.FC<SitesSettingsRowProps> = ({ withUsageCount }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedSite, setSelectedSite] = useState<Site | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const { data: sites, isLoading: isSitesLoading, refetch: refetchSites } = useSitesQuery({ queries: { withUsage: withUsageCount } });
    const { data: dataEntities, isLoading: entitiesIsLoading, isError: entitiesIsError, isSuccess: entitiesIsSuccess } = useEntitiesQuery({});

    const {
        mutate: mutateSite,
        isSuccess: isSuccessSite,
        isError: isErrorSite,
        isLoading: isLoadingSite,
        error: errorSite,
    } = useUpdateSiteMutation({
        onSuccess: () => refetchSites(),
        onError: (error) => console.error('Error updating site:', error.message),
    });

    const columns: ColDef<Site>[] = [
        { headerName: capitalizeFirstLetter(t('name')), field: 'name', initialFlex: 1, minWidth: 400, colId: 'name' },
        { headerName: capitalizeFirstLetter(t('codeProC')), field: 'codeProC', colId: 'codeProC', initialWidth: 150 },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<Site>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifySite'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedSite(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteSite'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedSite(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<Site>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('type')),
            valueGetter: (params) => capitalizeFirstLetter(getSiteTypeLabel(params.data?.type)),
            colId: 'type',
        },
        {
            headerName: capitalizeFirstLetter(t('entity')),
            valueGetter: (params) => dataEntities?.find((value) => value._id === params.data?.entityId)?.designation,
            colId: 'entity',
        },
        { headerName: capitalizeFirstLetter(t('address')), field: 'address', colId: 'address' },
        {
            headerName: capitalizeFirstLetter(t('workTravelZone')),
            field: 'workTravelZoneId.shortName',
            colId: 'workTravelZone',
            cellRenderer: (params: ICellRendererParams<Site>) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            width: 25,
                            height: 25,
                            borderRadius: '50%',
                            backgroundColor: params.data && params.data?.workTravelZoneId.travelPrice > 0 ? 'gold' : 'white',
                            flexShrink: 0,
                            color: '#000',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        {params.value}
                    </div>
                );
            },
        },
        {
            headerName: capitalizeFirstLetter(t('latitude')),
            field: 'latitude',
            colId: 'latitude',
            valueFormatter: (params) => formatNumber(params.value, t('locale'), 8),
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('longitude')),
            field: 'longitude',
            colId: 'longitude',
            valueFormatter: (params) => formatNumber(params.value, t('locale'), 8),
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('conduc')),
            valueGetter: (params) => formatName(params.data?.conducId?.firstName, params.data?.conducId?.lastName),
            colId: 'conduc',
        },
        {
            headerName: capitalizeFirstLetter(t('assistantConduc')),
            valueGetter: (params) => formatName(params.data?.assistantConducId?.firstName, params.data?.assistantConducId?.lastName),
            colId: 'assistantConduc',
        },
        {
            headerName: capitalizeFirstLetter(t('visibleInNeeds')),
            field: 'isVisibleInApp',
            colId: 'visibleInApp',
            valueFormatter: (params) => (params.value ? capitalizeFirstLetter(t('true')) : capitalizeFirstLetter(t('false'))),
        },
        {
            headerName: capitalizeFirstLetter(t('wasteProducer')),
            valueGetter: (params) => params.data?.wasteProducerId?.designation,
            colId: 'wasteProducer',
        },
        createDateColumn<Site>('startDate', 'startDate'),
        createDateColumn<Site>('endDate', 'endDate'),
        createDateTimeColumn<Site>('createdAt', 'createdAt'),
        createDateTimeColumn<Site>('updatedAt', 'updatedAt'),
        ...((user?.profileId.permsLevel === permissionLevelEnum.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                      initialWidth: 250,
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xTotal')),
                      field: 'usageCount.total',
                      colId: 'xtotal',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xRidesPlanned')),
                      field: 'usageCount.dailyTrucksPlanned',
                      colId: 'xridesplanned',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xRidesCompleted')),
                      field: 'usageCount.dailyTrucksRides',
                      colId: 'xridescompleted',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
              ]
            : []) as ColDef<Site>[]),
    ];

    const sortedSites = useMemo(() => {
        return sites?.sort((a, b) => a.name.localeCompare(b.name)) || [];
    }, [sites]);

    return (
        <>
            <GenericAgGridTable
                columnDefs={columns}
                rowData={sortedSites}
                loading={isSitesLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedSite(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addSite'))}
                    />
                }
                exportCsvFileName="admin-sites"
                storageKey={localStorageKeys.TABLE.ADMIN.INTERNAL.SITES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateSiteModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    site={selectedSite}
                    isEditMode={isEditMode}
                    refetch={refetchSites}
                />
            )}
            {showDeleteModal && selectedSite && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateSite({ siteId: selectedSite._id, data: { crudStatus: crudStatusEnum.DELETED } });
                    }}
                    title={t('site')}
                    itemName={selectedSite.name}
                />
            )}
        </>
    );
};

export default SitesSettingsRow;
